import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Bot, Send, User, BookmarkPlus, Share2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import { shareCardImage } from '@/lib/cardImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const americanToDec = (s) => {
  const m = String(s || '').match(/([+-]\d+)/);
  if (!m) return null;
  const a = parseInt(m[1], 10);
  return a > 0 ? 1 + a / 100 : 1 + 100 / Math.abs(a);
};

const combinedOdds = (picks) => {
  let dec = 1;
  for (const p of picks) {
    const d = americanToDec(p.odds);
    if (!d) return null;
    dec *= d;
  }
  const profit = dec - 1;
  return dec >= 2 ? `+${Math.round(profit * 100)}` : `-${Math.round(100 / profit)}`;
};

const shareBotPicks = async (picks) => {
  const card = {
    key: 'bot',
    title: `Parlay Bot ${picks.length}-Leg Play`,
    combined_odds: combinedOdds(picks) || '—',
    implied_hit_rate: '—',
    grade: 'AI',
    matchup_grade: '—',
    legs: picks.map(p => ({
      player: p.player,
      team: p.teamline || '',
      sport: '',
      prop: p.prop,
      odds: (String(p.odds || '').match(/([+-]\d+)/) || [])[1] || '',
    })),
  };
  try {
    const result = await shareCardImage(card, new Date().toLocaleDateString());
    if (result === 'downloaded') toast.success('Image downloaded — ready to post');
    else if (result === 'shared') toast.success('Shared');
  } catch (e) {
    toast.error('Could not create image');
  }
};

const SUGGESTIONS = [
  'Make me a 3 leg home run play',
  "What's the safest lock today?",
  'Build me a 4-leg mixed sport parlay',
  'Best WNBA points props tonight?',
];

const parseResponse = (text) => {
  const parts = [];
  const re = /::PICK::([\s\S]*?)::END::/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', content: text.slice(last, m.index).trim() });
    const block = m[1];
    const get = (label) => {
      const r = new RegExp(`${label}:\\s*(.+)`);
      const mm = block.match(r);
      return mm ? mm[1].trim() : null;
    };
    parts.push({
      type: 'pick',
      player: (get('PLAYER') || '').split('|')[0]?.trim(),
      teamline: (get('PLAYER') || '').split('|')[1]?.replace('TEAM:', '').trim(),
      prop: get('PROP'), odds: get('ODDS'), badges: get('BADGES'), why: get('WHY'),
    });
    last = re.lastIndex;
  }
  if (last < text.length) parts.push({ type: 'text', content: text.slice(last).trim() });
  return parts.filter(p => p.type === 'pick' || p.content);
};

const boldify = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
  seg.startsWith('**') ? <strong key={i} className="text-white">{seg.slice(2, -2)}</strong> : seg
);

const PickCard = ({ pick, headshots }) => {
  const hs = headshots?.[pick.player];
  return (
    <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-lg p-3 my-2" data-testid="bot-pick-card">
      <div className="flex items-center gap-3 mb-2">
        <img
          src={hs || 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=200&h=146'}
          alt={pick.player}
          className="w-10 h-10 rounded-full bg-[#112240] object-cover border border-brand-cyan shrink-0"
          onError={(e) => { e.target.src = 'https://a.espncdn.com/combiner/i?img=/i/headshots/nophoto.png&w=200&h=146'; }}
        />
        <div className="min-w-0">
          <div className="text-white font-semibold text-sm">{pick.player} <span className="text-muted-foreground font-normal text-xs">{pick.teamline}</span></div>
          <div className="text-brand-cyan text-xs">{pick.prop}</div>
        </div>
        <div className="ml-auto font-mono font-bold text-brand-lime text-sm shrink-0">{pick.odds}</div>
      </div>
      {pick.badges && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {pick.badges.split('|').map((b, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-brand-cyan/10 text-brand-cyan border border-brand-cyan/30">{b.trim()}</span>
          ))}
        </div>
      )}
      {pick.why && <p className="text-xs text-muted-foreground">{pick.why}</p>}
    </div>
  );
};

const SportsBot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('parlaybot_session') || null);
  const endRef = useRef(null);

  const saveSession = (sid) => {
    setSessionId(sid);
    if (sid) localStorage.setItem('parlaybot_session', sid);
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/sportsbot/history`, { params: { session_id: sessionId } })
      .then(res => {
        const msgs = res.data.messages || [];
        if (msgs.length) setMessages(msgs.map(m => ({ role: m.role, content: m.content, headshots: m.headshots })));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sportsbot`, { message: msg, session_id: sessionId });
      saveSession(res.data.session_id);
      setMessages(m => [...m, { role: 'bot', content: res.data.response, headshots: res.data.headshots || {} }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'bot', content: 'Something went wrong scanning the board. Try again in a moment.', headshots: {} }]);
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    localStorage.removeItem('parlaybot_session');
    setSessionId(null);
    setMessages([]);
    setInput('');
    toast.success('New chat started');
  };

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col">
      <Navigation />
      <main className="flex-1 pt-20 pb-4 px-4 sm:px-6 lg:px-8 flex flex-col">
        <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
              <Bot className="h-8 w-8 text-brand-lime" />
              Parlay Bot
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Remembers your conversation and scans every tab and live market price before every answer</p>
            </div>
            <Button
              onClick={newChat}
              variant="outline"
              className="border-[#1e3a5f] text-white hover:bg-[#112240] shrink-0"
              data-testid="bot-new-chat-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-4 mb-4 min-h-[300px]">
            {messages.length === 0 && (
              <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                <CardContent className="p-6">
                  <p className="text-white font-semibold mb-3">Ask me for picks — I scan the full board first.</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-[#1e3a5f] text-brand-cyan hover:bg-[#112240] transition-colors" data-testid={`bot-suggestion-${i}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] rounded-lg px-4 py-3 ${m.role === 'user' ? 'bg-brand-cyan text-[#0a1628]' : 'bg-[#0f1e36] border border-[#1e3a5f]'}`} data-testid={`bot-msg-${i}`}>
                  {m.role === 'user' ? (
                    <div className="flex items-start gap-2 text-sm font-medium"><User className="h-4 w-4 mt-0.5 shrink-0" />{m.content}</div>
                  ) : (
                    <div className="text-sm text-muted-foreground space-y-1">
                      {(() => {
                        const parts = parseResponse(m.content);
                        const picks = parts.filter(p => p.type === 'pick');
                        return (
                          <>
                            {parts.map((p, j) =>
                              p.type === 'pick'
                                ? <PickCard key={j} pick={p} headshots={m.headshots} />
                                : <p key={j} className="whitespace-pre-wrap leading-relaxed">{boldify(p.content)}</p>
                            )}
                            {picks.length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/bets`, {
                                      title: `Parlay Bot ${picks.length}-leg play`,
                                      legs: picks.map(p => ({ player: p.player, prop: p.prop, odds: p.odds })),
                                      source: 'sports_bot',
                                    }, { withCredentials: true });
                                    toast.success('Slip saved to My Bets');
                                  } catch (e) {
                                    toast.error(e.response?.status === 401 ? 'Log in to save slips' : 'Save failed');
                                  }
                                }}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-brand-lime/50 text-brand-lime hover:bg-brand-lime/10 transition-colors"
                                data-testid={`bot-save-slip-${i}`}
                              >
                                <BookmarkPlus className="h-3.5 w-3.5" /> Save Slip
                              </button>
                              <button
                                onClick={() => shareBotPicks(picks)}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-brand-cyan/50 text-brand-cyan hover:bg-brand-cyan/10 transition-colors"
                                data-testid={`bot-share-slip-${i}`}
                              >
                                <Share2 className="h-3.5 w-3.5" /> Share
                              </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Bot className="h-4 w-4 text-brand-lime animate-pulse" />
                Scanning the board — hit rates, matchups & live odds...
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="e.g. Make me a 3 leg home run play"
              className="bg-[#0f1e36] border-[#1e3a5f] text-white"
              data-testid="sportsbot-input"
            />
            <Button onClick={() => send()} disabled={busy} className="bg-brand-lime text-[#0a1628] hover:bg-brand-lime/90 font-bold" data-testid="sportsbot-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SportsBot;
